import { useState } from "react";
import { toast } from "sonner";
import { Check, AlertTriangle } from "lucide-react";
import { submitFeedback } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export const FeedbackForm = ({ assessmentId, existingFeedback }) => {
  const { t } = useLang();
  const fb = t.feedback;
  const [values, setValues] = useState({});
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(
    !!(existingFeedback && (existingFeedback.accuracy || existingFeedback.identified_real || existingFeedback.actions_usefulness || existingFeedback.comment))
  );
  const [saving, setSaving] = useState(false);

  const set = (id, v) => setValues((p) => ({ ...p, [id]: v }));
  const hasAnswer = Object.values(values).some(Boolean) || comment.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    try {
      await submitFeedback(assessmentId, { ...values, comment });
      setDone(true);
      toast.success(fb.success);
    } catch (e) {
      toast.error(fb.error);
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-[#E2DFD8] p-8 md:p-12 flex items-center gap-4" data-testid="feedback-thanks">
        <div className="w-10 h-10 border border-[#475F51] flex items-center justify-center shrink-0">
          <Check className="w-5 h-5 text-[#475F51]" strokeWidth={1.75} />
        </div>
        <p className="text-base text-[#2C363F]">{fb.thanks}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2DFD8] p-8 md:p-12" data-testid="feedback-form">
      <div className="flex flex-col gap-10">
        {fb.questions.map((q, idx) => (
          <div key={q.id} data-testid={`feedback-${q.id}`}>
            {q.id !== "accuracy" && <p className="text-base text-[#0B0E14] mb-4">{q.label}</p>}
            <div className="flex flex-wrap gap-2">
              {q.options.map((opt) => {
                const selected = values[q.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => set(q.id, opt.value)}
                    data-testid={`feedback-${q.id}-${opt.value}`}
                    className={`px-5 py-3 text-sm border transition-colors ${
                      selected
                        ? "bg-[#0B0E14] text-white border-[#0B0E14]"
                        : "bg-white text-[#4A5568] border-[#E2DFD8] hover:border-[#0B0E14]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div>
          <p className="text-base text-[#0B0E14] mb-2">{fb.commentLabel}</p>
          <div className="flex items-start gap-2 mb-4 bg-[#F6F5F2] border-l-2 border-[#A68A56] px-3 py-2" data-testid="comment-warning">
            <AlertTriangle className="w-4 h-4 text-[#A68A56] mt-0.5 shrink-0" strokeWidth={1.75} />
            <p className="text-xs text-[#4A5568] leading-relaxed">{fb.commentWarning}</p>
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            data-testid="feedback-comment"
            placeholder={fb.optional}
            className="w-full bg-[#F6F5F2] border border-[#E2DFD8] p-4 text-base text-[#0B0E14] placeholder:text-[#A0AEC0] focus:outline-none focus:border-[#0B0E14] transition-colors resize-none"
          />
        </div>

        <div>
          <button
            onClick={submit}
            disabled={saving || !hasAnswer}
            data-testid="submit-feedback-btn"
            className="bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fb.submit}
          </button>
        </div>
      </div>
    </div>
  );
};
